import { Component, ViewChild, inject } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { BehaviorSubject, first, iif, Observable, of, tap } from 'rxjs';
import { SearchComponent } from 'src/app/core/components/search/search.component';
import { Constants } from 'src/app/core/constants';
import { CustomFile } from 'src/app/core/models/customFile.interface';
import { MinimalOrganization } from 'src/app/core/models/organization/minimal-organization.interface';
import { ProjectFormModel } from 'src/app/core/models/project/project-form.model';
import { MinimalProfile } from 'src/app/core/models/user/profile';
import { FileService } from 'src/app/core/services/file.service';
import { KeyValueSort } from 'src/app/core/utils/key-value-sort';
import { OrganizationSearchConfig } from 'src/app/core/utils/organization-search-util';

@Component({
    selector: 'app-editable-project',
    template: '',
    standalone: false
})
export abstract class EditableProjectComponent {
  @ViewChild(SearchComponent)
  private employeesSearchComponent?: SearchComponent<MinimalProfile>;

  public readonly acceptedExtensions = Constants.ImageExtensions;
  public readonly organizationSearchConfig = OrganizationSearchConfig;
  public readonly employeeSearchConfig = {
    filterFn: (item: MinimalProfile, term: string): boolean => {
      const searchTerm = term.trim().toLowerCase();
      if (!searchTerm) {
        return true;
      }

      const fullName = (item.fullName ?? '').toLowerCase();
      const email = (item.email ?? '').toLowerCase();
      return fullName.includes(searchTerm) || email.includes(searchTerm);
    },
    formatter: (item: MinimalProfile | null | undefined): string =>
      item?.fullName ?? '',
    compareFn: (item: MinimalProfile, value: MinimalProfile): boolean =>
      item.id === value.id,
  };

  public projectImage?: File;
  public projectImagePath = '';

  public tags = Constants.ProjectTags;
  public tagsSortOrder = KeyValueSort.valueAscOrder;

  public projectFormModel: ProjectFormModel;

  // These fields just for ease of use on template
  public titleFormControl: AbstractControl | null;
  public descriptionFormControl: AbstractControl | null;
  public tagFormControl: AbstractControl | null;

  public selectedEmployee: MinimalProfile | null = null;
  public selectedEmployeeControl = new FormControl<string | null>(null);

  public projectEmployees$ = new BehaviorSubject<MinimalProfile[]>([]);
  public companyEmployees$ = new BehaviorSubject<MinimalProfile[]>([]);
  public companyOrganizations$ = new BehaviorSubject<MinimalOrganization[]>([]);
  public previewUrl$ = new Observable<string>();

  protected fileService = inject(FileService);

  constructor() {
    this.projectFormModel = inject(ProjectFormModel);

    this.titleFormControl = this.projectFormModel.form.controls.title;
    this.descriptionFormControl =
      this.projectFormModel.form.controls.description;
    this.tagFormControl = this.projectFormModel.form.controls.tag;
  }

  public onSelectEmployee(employee: MinimalProfile | null): void {
    if (!employee) {
      this.selectedEmployee = null;
      return;
    }

    const isInCompanyList = this.companyEmployees$
      .getValue()
      .some((e) => e.id === employee.id);
    const isAlreadyAdded = this.projectEmployees$
      .getValue()
      .some((e) => e.id === employee.id);

    this.selectedEmployee =
      isInCompanyList && !isAlreadyAdded ? employee : null;
  }

  private clearEmployeeSearchInput(): void {
    this.selectedEmployee = null;
    this.selectedEmployeeControl.setValue(null);

    this.employeesSearchComponent?.form.reset();
  }

  public addEmployee(employee: MinimalProfile): void {
    const isInCompanyList = this.companyEmployees$
      .getValue()
      .some((e) => e.id === employee.id);
    const isAlreadyAdded = this.projectEmployees$
      .getValue()
      .some((e) => e.id === employee.id);

    if (!isInCompanyList || isAlreadyAdded) {
      this.clearEmployeeSearchInput();
      return;
    }

    this.clearEmployeeSearchInput();

    const employeeProfile: MinimalProfile = {
      ...employee,
      imageUrl: Constants.NotFound,
    };

    const employeeWithImage$ = iif(
      () => !!employeeProfile.avatarImageExtension,
      this.fileService.getEntityWithImageUrl(
        employeeProfile,
        `users/${employeeProfile.id}/avatar${employeeProfile.avatarImageExtension}`,
      ),
      of(employeeProfile),
    );

    employeeWithImage$
      .pipe(
        first(),
        tap((employee) => {
          this.projectEmployees$.next([
            ...this.projectEmployees$.getValue(),
            { ...employee },
          ]);

          this.clearEmployeeSearchInput();

          const employees = this.companyEmployees$
            .getValue()
            .filter((e) => e.id !== employee.id);

          this.companyEmployees$.next(employees);
        }),
      )
      .subscribe();
  }

  public removeEmployee(employeeProfile: MinimalProfile): void {
    const employees = this.projectEmployees$
      .getValue()
      .filter((e) => e.id !== employeeProfile.id);

    this.projectEmployees$.next(employees);

    const companyEmployees = this.companyEmployees$.getValue();
    const alreadyInCompanyList = companyEmployees.some(
      (employee) => employee.id === employeeProfile.id,
    );

    if (!alreadyInCompanyList) {
      this.companyEmployees$.next([...companyEmployees, employeeProfile]);
    }
  }

  public abstract onUploadFile(event: CustomFile): void;
  public abstract onDeleteFile(): void;
}
